import { Typography, Button, Box, Dialog, useTheme, IconButton, CircularProgress, Tooltip, useMediaQuery } from '@mui/material'
import ResourceInput from './ResourceInput'
import React, { useCallback, useEffect, useState } from 'react'
import { AddRounded, CheckBox, CheckBoxOutlineBlank, DownloadOutlined, Remove, UploadOutlined } from '@mui/icons-material'
import { useLiveSessionStore } from '../../../store/livesessions.store'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import { getFileSizeFromBytes, Icons } from '../../../utils/utilities'
import ConfirmDialog from '../../../components/common/ConfirmDialog'
import noResource from '../../../assets/no-resources.svg'
import Progress from '../../common/Progress'
import CustomChip from '../../common/CustomChip'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import { toast } from 'react-toastify'

const StorageStatusDetails = observer( () => {

    const LiveSessionStore = useLiveSessionStore()

    const { layout, border, palette } = useTheme()

    const currentResourcesSize = LiveSessionStore.getResources.reduce( ( acc, item ) => acc + ( +item?.resource_metadata?.size || 0 ), 0 ) || 0
    const totalSizeAlloted = 1024 * 1024 * 1024
    const usedSpace = Math.ceil( ( currentResourcesSize * 100 ) / totalSizeAlloted )

    return <Box bgcolor={( usedSpace > 90 ? palette.error.main : usedSpace > 75 ? palette.warning.main : palette.primary.main ) + "11"} border={border[1]} borderColor={usedSpace > 90 ? palette.error.main : usedSpace > 75 ? palette.warning.main : palette.primary.main} display="flex" gap={layout.cardsGap} alignItems="center" padding="10px" borderRadius={layout.borderRadius}>
        <Progress icon={Icons.default.Storage} color={usedSpace > 90 ? palette.error.main : usedSpace > 75 ? palette.warning.dark : palette.primary.main} size={50} trackWidth={8} indicatorWidth={6} progress={usedSpace} />
        <Box>
            <Typography fontWeight="900" variant='h6' color={usedSpace > 90 ? "error" : usedSpace > 75 ? "warning" : "primary"} fontSize="1rem">
                {getFileSizeFromBytes( currentResourcesSize )} of {getFileSizeFromBytes( totalSizeAlloted )} account storage used
            </Typography>
            <Typography variant='caption' display="block">
                Your account has a total storage limit of {getFileSizeFromBytes( totalSizeAlloted )}, shared across all the resources you upload. This is a combined limit, not a per-file limit.
                To use the same resource in another session, use <b>Import</b> instead of uploading it again. Imported resources don't take up additional space.
            </Typography>
            <Typography variant='caption' display="block" marginTop="4px">
                Need more storage? Please contact the <b>Alive co-ordinators</b>.
            </Typography>
        </Box>
    </Box>
} )


const AddResourcesDialog = observer( ( { classSession, state, setState, filter, setLinkMode } ) => {

    const [resourceSelectedForDeletion, setResourceSelectedForDeletion] = useState( null )
    const [resourceFilter, setResourceFilter] = useState( 'available' )
    const [selectedResources, setSelectedResources] = useState( [] )
    const [markedResources, setMarkedResources] = useState( [] )
    const [uploading, setUploading] = useState( false )
    const [importing, setImporting] = useState( false )
    const [deleting, setDeleting] = useState( false )
    const [loading, setLoading] = useState( true )
    const [mode, setMode] = useState( 'upload' )

    const LiveSessionStore = useLiveSessionStore()
    const currentResourcesSize = LiveSessionStore.getResources.reduce( ( acc, item ) => acc + ( +item?.resource_metadata?.size || 0 ), 0 ) || 0
    const selectedResourcesSize = selectedResources.reduce( ( acc, item ) => acc + ( +item.size || 0 ), 0 ) || 0
    const disbaleExtraSelection = currentResourcesSize + selectedResourcesSize > 1024 * 1024 * 1024
    const showSelectedDeleteButton = markedResources.length > 1 && markedResources.every( resourceId => {
        const resourceDetails = LiveSessionStore.getResources.find( res => res.id === resourceId )
        return resourceDetails?.linked_session_ids?.length === 0
    } )

    const { palette, border, layout, breakpoints, shadows } = useTheme()

    const sm = useMediaQuery( breakpoints.down( 'sm' ) )


    const handleClose = () => {
        setState( false )
        setSelectedResources( [] )
        setMarkedResources( [] )
    }

    const filterFunc = r => {
        if ( resourceFilter === 'all' ) return true
        if ( resourceFilter === 'available' ) return !r.linked_session_ids?.includes( classSession.id )
        if ( resourceFilter === 'in use' ) return r.linked_session_ids?.includes( classSession.id )
    }

    const toggleMark = ( id, present ) => {
        if ( present >= 0 ) {
            let updated = [...markedResources]
            updated.splice( updated.indexOf( id ), 1 )
            setMarkedResources( updated )
        } else {
            let updated = [...markedResources, id]
            setMarkedResources( updated )
        }
    }

    const importResources = async () => {
        setImporting( true )
        const resources = markedResources?.map( resource => { return { id: resource, category: filter } } )
        if ( await LiveSessionStore.linkResource( classSession.id, resources ) ) {
            LiveSessionStore.fetchResources()
            handleClose()
        }
        setImporting( false )
    }

    const minimizeDialog = () => {
        LiveSessionStore.minimizeResourceUploadDialog()
        handleClose()
    }

    const uploadResources = async () => {
        for ( let i = 0; i < selectedResources.length; i++ ) {
            const { name } = selectedResources[i]
            if ( !name || name?.trim() === "" ) {
                return toast( "Please provide names to all resources." )
            }
        }
        setUploading( true )

        LiveSessionStore.setUploadsList( selectedResources )

        await Promise.allSettled( selectedResources.map( async resource => {
            const formData = new FormData()
            formData.append( 'name', resource?.name?.split( "." )[0] || "Unknown Resource" )
            formData.append( 'size', resource?.size )
            formData.append( 'resource_type', resource.type )
            formData.append( 'session_category', 'IN CLASS' )
            formData.append( 'session_id', classSession.id )
            formData.append( 'resource', resource.type === 'link' ? JSON.stringify( resource ) : resource.resource )
            return await LiveSessionStore.addResource( formData, classSession.id, resource )
        } ) )

        LiveSessionStore.fetchLiveSession( classSession.id )
        LiveSessionStore.fetchResources()

        setUploading( false )
        handleClose()
    }

    const deleteResource = async ( id ) => {
        setDeleting( true )
        const success = await LiveSessionStore.deleteResource( id )
        setDeleting( false )
        if ( success ) {
            setLoading( true )
            await LiveSessionStore.fetchResources()
            if ( markedResources.includes( id ) )
                setMarkedResources( prev => {
                    const newMarkedList = [...prev]
                    const index = newMarkedList.findIndex( mr => mr === id )
                    if ( index >= 0 )
                        newMarkedList.splice( index, 1 )
                    return newMarkedList
                } )
            setLoading( false )
        }
        setResourceSelectedForDeletion( null )
    }

    const getData = useCallback( async () => {
        setLoading( true )
        if ( state === true && LiveSessionStore.getErrors.resources ) {
            await LiveSessionStore.fetchResources()
        }
        setLoading( false )
    }, [state, LiveSessionStore] )

    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Dialog fullScreen={sm} open={state} onClose={handleClose} slotProps={{
            container: { sx: { alignItems: { xs: "flex-end", md: "center" } } },
            paper: { sx: { height: "fit-content", maxHeight: "90vh", margin: 0, width: { md: "75%", xs: "100%" }, maxWidth: mode === 'upload' ? "720px" : "1024px", display: "flex", flexDirection: "column", gap: layout.sectionsGap, borderRadius: { md: "10px", xs: "10px 10px 0 0" } } }
        }}>
            <ConfirmDialog waiting={deleting} message="The resource will be removed from all associated sessions." status={Boolean( resourceSelectedForDeletion )} actionName="Yes! delete it" confirmAction={() => deleteResource( resourceSelectedForDeletion )} cancelAction={() => setResourceSelectedForDeletion( null )} />
            <Box display="flex" flexDirection="column" gap={layout.sectionsGap} padding={layout.pagePadding}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box >
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Add new resources to {classSession.name}</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Select files from your computer to upload resources.</Typography>
                    </Box>
                    <Box gap={layout.cardsGap} sx={{ display: 'flex', alignItems: 'center', color: 'black', padding: '0' }}>
                        {mode === "upload" && <Button onClick={() => setMode( "import" )} color='secondary' startIcon={Icons.default.DownloadIcon} size="small" variant='outlined'>Switch to import mode</Button>}
                        {mode === "import" && <Button onClick={() => setMode( "upload" )} color='secondary' startIcon={Icons.default.CloudUpload} size="small" variant='outlined'>Switch to upload mode</Button>}
                        {uploading && <Button startIcon={<Remove />} sx={{ textTransform: "capitalize" }} variant='text' color='primaryDark' onClick={minimizeDialog}>
                            minimize
                        </Button>}
                        <IconButton disabled={uploading || importing} variant='text' color='primaryDark' onClick={handleClose}>
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Box>
                </Box>

                {!LiveSessionStore.getErrors.resources && <StorageStatusDetails />}
                {disbaleExtraSelection && mode === "upload" && <Box bgcolor={palette.warning.main + "11"} border={border[1]} borderColor={palette.warning.main} gap={layout.cardsGap} padding="10px" borderRadius={layout.borderRadius}>
                    <Box display="flex" gap="10px" alignItems="flex-start">
                        <Box fontSize="38px" color={palette.warning.main}>
                            {Icons.default.Warning}
                        </Box>
                        <Typography display='block' fontWeight="900" variant='subtitle2' color="warning">
                            The selected resources exceed your account's remaining storage. Please delete unused resources to free up space, or contact the Alive co-ordinators if you need more storage.
                        </Typography>
                    </Box>
                    <Button onClick={() => {
                        setMode( "import" )
                        setResourceFilter( 'available' )
                    }} variant='outlined' sx={{ marginTop: "10px" }} size="small" >View unused resource</Button>
                </Box>}

                {mode === 'upload' ? <Box>
                    <ResourceInput
                        class_session_id={classSession.id}
                        uploading={uploading}
                        disabled={disbaleExtraSelection}
                        setUploading={setUploading}
                        setState={setState}
                        filter={filter}
                        handleClose={handleClose}
                        selectedResources={selectedResources}
                        setSelectedResources={setSelectedResources}
                    />
                </Box> :
                    <Box >
                        {loading && !LiveSessionStore.getErrors.resources && <DataLoadingSpinner waitingMessage="Loading resources..." />}
                        {!loading && !LiveSessionStore.getErrors.resources && LiveSessionStore.getResources.length > 0 && <Box border={border[1]} borderRadius={layout.borderRadius}>
                            <Box borderBottom={`1px solid ${palette.borderColor}`} display="flex" gap="10px" height="40px" padding="5px">
                                <Typography width="100px" display="flex" alignItems="center" justifyContent="center" textAlign="center" onClick={() => setResourceFilter( 'available' )} sx={{ color: resourceFilter === "available" ? `${palette.primary.contrastText}` : 'inherit', cursor: "pointer", borderRadius: "3px", }} padding="3px" bgcolor={resourceFilter === "available" ? `#5E2974` : palette.common.default} variant="subtitle2">Available</Typography>
                                <Typography width="100px" display="flex" alignItems="center" justifyContent="center" textAlign="center" onClick={() => setResourceFilter( 'in use' )} sx={{ color: resourceFilter === "in use" ? `${palette.primary.contrastText}` : 'inherit', cursor: "pointer", borderRadius: "3px", }} padding="3px" bgcolor={resourceFilter === "in use" ? `#5E2974` : palette.common.default} variant="subtitle2">In use</Typography>
                                <Typography width="100px" display="flex" alignItems="center" justifyContent="center" textAlign="center" onClick={() => setResourceFilter( 'all' )} sx={{ color: resourceFilter === "all" ? `${palette.primary.contrastText}` : 'inherit', cursor: "pointer", borderRadius: "3px", }} padding="3px" bgcolor={resourceFilter === "all" ? `#5E2974` : palette.common.default} variant="subtitle2">All</Typography>
                            </Box>
                            <Box >
                                <Box display="flex" gap="10px" alignItems="center" padding="10px" paddingTop="20px">
                                    <CustomChip textStyles={{ textTransform: "capitalize", padding: "2px 0" }} value={`${resourceFilter}: ${LiveSessionStore.getResources.filter( filterFunc ).length}`} />
                                    <CustomChip value={`Selected: ${markedResources.length}`} />
                                </Box>
                                <Box padding="10px" gridTemplateColumns="repeat(auto-fit,minmax(300px,1fr))" display={{ md: "grid", xs: "flex" }} gap="10px" flexWrap="wrap">
                                    {LiveSessionStore.getResources.filter( filterFunc ).map( ( resource, index ) => {
                                        const marked = markedResources.indexOf( resource.id )
                                        return (
                                            <Box padding="10px" width="100%" overflow="hidden" maxWidth={{ md: "350px", xs: "initial" }} flexGrow={{ md: "initial", xs: 1 }} display="flex" gap="10px" borderRadius="5px" alignItems="flex-start" border={border[1]} key={resource.id}>
                                                {!resource.linked_session_ids?.includes( classSession.id ) && <IconButton disabled={importing} onClick={() => toggleMark( resource.id, marked )} color={marked ? "primary" : "greyed"} size="small">
                                                    {marked >= 0 ? <CheckBox color={"primaryDark"} /> : <CheckBoxOutlineBlank color="greyed" />}
                                                </IconButton>}
                                                {resource.linked_session_ids?.includes( classSession.id ) && <IconButton disabled color="greyed" size="small">
                                                    <CheckBox />
                                                </IconButton>}
                                                <Box display="flex" minWidth={0} flexGrow={1} overflow="hidden" alignItems="center" justifyContent="space-between" gap="20px">
                                                    <Box overflow="hidden" minWidth={0}>
                                                        <Typography gutterBottom noWrap title={resource.name} display="block" variant='subtitle2'>{resource.name}</Typography>
                                                        <Box display="flex" flexWrap="wrap" gap="5px" alignItems="center" >
                                                            <CustomChip textStyles={{ textTransform: "capitalize" }} tooltipTitle='Resource Type' value={resource.resource_type} />
                                                            {resource.resource_type !== 'link' && <CustomChip tooltipTitle='Resource Size' value={getFileSizeFromBytes( resource.resource_metadata?.size )} />}
                                                        </Box>
                                                    </Box>
                                                    <Tooltip title={resource.linked_session_ids?.length > 0 ? "This resource is linked to atleast one session! Linked resource cannot be deleted. Remove from linked session to delete this resource." : "Delete resource"}>
                                                        <span>
                                                            <IconButton disabled={importing || resource.linked_session_ids?.length > 0} onClick={() => setResourceSelectedForDeletion( resource.id )} color="error" size="small">
                                                                {Icons.default.DeleteIconOutlined}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        )
                                    } )}
                                    {LiveSessionStore.getResources.filter( filterFunc ).length === 0 && <Typography border={border[1]} flexGrow={1} borderRadius="5px" bgcolor={palette.contentBg} padding="20px" variant='subtitle2'>
                                        No resources in this section
                                    </Typography>}
                                </Box>
                                {/* <Box display="flex" gap="20px" padding="20px 10px" alignItems="center">
                                <Button disableElevation disabled={importing} variant='contained' color="primary" onClick={importResources} startIcon={importing ? <CircularProgress size={14} /> : Icons.default.DownloadIcon} >
                                    Import
                                </Button>
                                <Button disabled={importing} disableElevation variant="contained" color='error' onClick={handleClose}>
                                    Cancel
                                </Button>
                            </Box> */}
                            </Box>
                        </Box>}
                        {!loading && !LiveSessionStore.getErrors.resources && LiveSessionStore.getResources.length === 0 && <Box bgcolor={palette.contentBg} borderRadius="5px" padding="50px 20px" display="flex" flexDirection="column" gap="20px" alignItems="center" justifyContent="center">
                            <img src={noResource} width="120px" alt="No resources present" />
                            <Typography variant="subtitle2">No resources to show!  </Typography>
                            <Typography variant='subtitle2' sx={{ color: "grey", fontSize: "14px", cursor: "pointer", '&:hover': { color: "darkgrey" } }} onClick={() => setLinkMode( 0 )} alignItems="center" display="flex" gap="5px">
                                <AddRounded />
                                Add resources
                            </Typography>
                        </Box>}
                        {LiveSessionStore.getErrors.resources && <TryAgainMessageBlock loading={loading} getData={getData} err={LiveSessionStore.getErrors.resources} />}
                    </Box>}
            </Box>

            <Box position="sticky" borderTop={border[1]} bgcolor={palette.contentBg} padding="10px" alignItems="center" boxShadow={shadows[1]} bottom="0" display="flex" gap={layout.cardsGap} justifyContent="space-between">
                {selectedResources.length > 0 ? <Typography variant='subtitle2' >{selectedResources.length} resource{selectedResources.length > 1 ? "s" : ""} selected </Typography> : <Box />}
                <Box display="flex" gap={layout.cardsGap} justifyContent="space-between">
                    <Button color='error' size="small" onClick={handleClose} startIcon={Icons.default.CloseIcon} variant='outlined'>Cancel</Button>
                    {selectedResources.length === 0 && mode === "upload" && <Button onClick={() => setMode( "import" )} color='secondary' startIcon={Icons.default.DownloadIcon} size="small" variant='outlined'>Switch to import mode</Button>}
                    {markedResources.length === 0 && mode === "import" && <Button onClick={() => setMode( "upload" )} color='secondary' startIcon={Icons.default.CloudUpload} size="small" variant='outlined'>Switch to upload mode</Button>}
                    {selectedResources.length > 0 && <Button color='primary' size="small" disabled={disbaleExtraSelection || uploading} onClick={uploadResources} startIcon={uploading ? <CircularProgress size={20} /> : Icons.default.CloudUpload} variant='contained' disableElevation>Upload Resources</Button>}
                    {showSelectedDeleteButton && <Button color='error' size="small" disabled={importing} onClick={() => { }} startIcon={importing ? <CircularProgress size={20} /> : Icons.default.DeleteIconOutlined} variant='contained' disableElevation>Delete Selected Resources</Button>}
                    {markedResources.length > 0 && <Button color='primary' size="small" disabled={importing} onClick={importResources} startIcon={importing ? <CircularProgress size={20} /> : Icons.default.DownloadIcon} variant='contained' disableElevation>Import Selected Resources</Button>}
                </Box>
            </Box>
        </Dialog>
    )
} )

export default AddResourcesDialog